import React, { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import * as Checkbox from "@radix-ui/react-checkbox";
import * as Select from "@radix-ui/react-select";
import { Check, ChevronDown } from "lucide-react";

type SelectOption = string | { value: string; label: string };

interface CommonProps {
  name?: string;
  label?: string;
  wrapperClass?: string;
  inputClass?: string;
  error?: string;
  requiredIcon?: boolean;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
}

type InputFieldProps = CommonProps &
  Omit<React.InputHTMLAttributes<HTMLInputElement>, "prefix"> & {
    type?: "text" | "password" | "email" | "number" | "date" | "time";
  };

type TextareaFieldProps = CommonProps &
  React.TextareaHTMLAttributes<HTMLTextAreaElement> & {
    type: "textarea";
    rows?: number;
  };

type SelectFieldProps = CommonProps & {
  type: "select";
  options: SelectOption[];
  value?: string;
  onValueChange?: (value: string) => void;
  disabled?: boolean;
};

type CheckboxRadioProps = CommonProps & {
  type: "checkbox";
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
};

type FormFieldProps =
  | InputFieldProps
  | TextareaFieldProps
  | SelectFieldProps
  | CheckboxRadioProps;

const FormField: React.FC<FormFieldProps> = (props) => {
  const {
    label,
    error,
    wrapperClass = "mb-4",
    inputClass = "w-full h-10 px-3 py-2 rounded-md border bg-background text-sm text-foreground",
    requiredIcon = false,
  } = props;

  const id = props.name || label || "field";
  const [showPassword, setShowPassword] = useState(false);

  const renderLabel = () =>
    label ? (
      <label className="block text-sm font-medium text-foreground mb-1.5">
        {label} {requiredIcon && <span className="text-red-500">*</span>}
      </label>
    ) : null;

  /* ---------- RADIX SELECT ---------- */
  if (props.type === "select") {
    const { options, value, onValueChange, disabled } = props;

    // If value is empty and there's no matching empty option, pass undefined so Radix shows the placeholder
    const hasEmptyOption = options.some(opt => typeof opt === "string" ? opt === "" : opt.value === "");
    const finalValue = (value === "" && !hasEmptyOption) ? undefined : value;

    return (
      <div className={wrapperClass}>
        {renderLabel()}

        <Select.Root value={finalValue} onValueChange={onValueChange} disabled={disabled}>
          <Select.Trigger
            className={`${inputClass} flex items-center justify-between ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <Select.Value placeholder={`Select ${label}`} />
            <ChevronDown size={16} />
          </Select.Trigger>

          <Select.Portal>
            <Select.Content className="bg-white border rounded-md shadow-md z-50">
              <Select.Viewport>
                {options.map((opt, i) => {
                  const value =
                    typeof opt === "string" ? opt : opt.value;
                  const label =
                    typeof opt === "string" ? opt : opt.label;

                  return (
                    <Select.Item
                      key={i}
                      value={value}
                      className="px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 flex justify-between"
                    >
                      <Select.ItemText>{label}</Select.ItemText>
                      <Select.ItemIndicator>
                        <Check size={14} />
                      </Select.ItemIndicator>
                    </Select.Item>
                  );
                })}
              </Select.Viewport>
            </Select.Content>
          </Select.Portal>
        </Select.Root>
        {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
      </div>
    );
  }

  /* ---------- TEXTAREA ---------- */
  if (props.type === "textarea") {
    const { rows = 5, ...textareaProps } = props;
    // Remove fixed height 'h-10' for textareas so 'rows' attribute works
    const textareaClass = inputClass.replace("h-10", "h-auto min-h-[100px]");

    return (
      <div className={wrapperClass}>
        {renderLabel()}

        <textarea
          {...textareaProps}
          id={id}
          rows={rows}
          className={textareaClass}
        />
        {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
      </div>
    );
  }

  /* ---------- RADIX CHECKBOX ---------- */
  if (props.type === "checkbox") {
    const { checked, onCheckedChange } = props;

    return (
      <div className="flex items-center gap-2 mb-3">
        <Checkbox.Root
          checked={checked}
          onCheckedChange={onCheckedChange}
          className="w-5 h-5 border rounded flex items-center justify-center"
        >
          <Checkbox.Indicator>
            <Check size={14} />
          </Checkbox.Indicator>
        </Checkbox.Root>

        {renderLabel()}
      </div>
    );
  }

  /* ---------- INPUT ---------- */
  const inputType =
    props.type === "password"
      ? showPassword
        ? "text"
        : "password"
      : props.type || "text";

  const inputProps = props as InputFieldProps;
  // Destructure ALL custom/non-native props so they are never forwarded to the DOM <input>
  const {
    prefix,
    suffix,
    wrapperClass: _wrapperClass,
    inputClass: _inputClass,
    requiredIcon: _requiredIcon,
    label: _label,
    error: _error,
    ...restInputProps
  } = inputProps;

  return (
    <div className={wrapperClass}>
      {renderLabel()}

      <div className="relative flex items-center">
        {prefix && (
          <div className="absolute left-3 text-muted-foreground pointer-events-none">
            {prefix}
          </div>
        )}
        <input
          {...restInputProps}
          id={id}
          type={inputType}
          className={`${inputClass} ${prefix ? "pl-9" : ""} ${suffix || props.type === "password" ? "pr-10" : ""
            }`}
        />
        {suffix && props.type !== "password" && (
          <div className="absolute right-3 text-muted-foreground pointer-events-none">
            {suffix}
          </div>
        )}

        {props.type === "password" && (
          <button
            type="button"
            className="absolute top-1/2 right-3 -translate-y-1/2"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        )}
      </div>
      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
  );
};


export default FormField;